import { useEffect, useState } from 'react';

/**
 * Tiny hash-based router (no router dependency — offline, zero-config, Pages-friendly). Three
 * screens (PLAN.md M7): the plan editor, the results dashboard, and the ledger drill-down. Hash
 * routing means `vite build` produces a single static `index.html` that works from any path on
 * Cloudflare Pages without SPA-rewrite config.
 */

export type Route = 'editor' | 'results' | 'ledger';

export const ROUTES: ReadonlyArray<{ id: Route; label: string; hash: string }> = [
  { id: 'editor', label: 'Plan', hash: '#/plan' },
  { id: 'results', label: 'Results', hash: '#/results' },
  { id: 'ledger', label: 'Ledger', hash: '#/ledger' },
];

function routeFromHash(hash: string): Route {
  if (hash.startsWith('#/results')) return 'results';
  if (hash.startsWith('#/ledger')) return 'ledger';
  return 'editor';
}

/** Read the current route and subscribe to hash changes. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => routeFromHash(window.location.hash));
  useEffect(() => {
    const onHash = (): void => setRoute(routeFromHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return route;
}

/** Navigate to a route (sets the hash; the listener updates state). */
export function navigate(route: Route): void {
  const target = ROUTES.find((r) => r.id === route);
  if (target) window.location.hash = target.hash;
}
